import { Draggable } from '@hello-pangea/dnd'
import { MessageSquare, Pencil, Sailboat } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'

import type { Deal } from '@/types/deal'
import { STAGE_COLUMN } from '@/types/deal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/format'
import { customerDisplayName } from '@/features/customers/customer-utils'

interface DealCardProps {
  deal: Deal
  index: number
  onEdit?: (deal: Deal) => void
  onMessageClick?: (deal: Deal) => void
}

export function DealCard({ deal, index, onEdit, onMessageClick }: DealCardProps) {
  const customerName = deal.customer ? customerDisplayName(deal.customer) : '—'
  const navigate = useNavigate()

  return (
    <Draggable draggableId={deal.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          // Click opens the deal's detail page; editing is an explicit action.
          onClick={() => navigate({ to: '/sales/deals/$dealId', params: { dealId: deal.id } })}
          className="cursor-grab active:cursor-grabbing"
        >
          <Card
            className={`overflow-hidden transition-shadow ${
              snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/40' : ''
            }`}
          >
            <div className={`h-1 w-full ${STAGE_COLUMN[deal.stage]}`} />
            <CardHeader className="flex flex-row items-start justify-between space-y-0 p-3 pb-1">
              <CardTitle className="text-sm leading-tight">{deal.title}</CardTitle>
              <div className="-mr-1 -mt-1 flex shrink-0">
                {onEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label={`Edit ${deal.title}`}
                    // Stop the pointer events so the button neither starts the
                    // card drag nor triggers the card's navigation onClick.
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit(deal)
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {onMessageClick && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label={`Text customer about ${deal.title}`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      onMessageClick(deal)
                    }}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-1">
              <div className="flex items-center justify-between">
                <span className="truncate text-xs text-muted-foreground">{customerName}</span>
                <span className="ml-2 shrink-0 text-sm font-semibold">
                  {formatCurrency(deal.value)}
                  {deal.addons_total > 0 && (
                    <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                      +add-ons
                    </span>
                  )}
                </span>
              </div>
              {deal.boat && (
                <div className="mt-1 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                  <Sailboat className="h-3 w-3 shrink-0" />
                  {deal.boat.make} {deal.boat.model}
                </div>
              )}
              <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{deal.owner}</span>
                {deal.expected_close && (
                  <span>
                    {new Date(deal.expected_close).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
  )
}
